<template>
  <ul
    v-if="items.length"
    :class="['le-select-list',`bubble-list-align-${bubbleListAlign}`,
    { 'floated-list': floatedList, 'top-direction': openListOnTop, 'bubble-list': bubbleList, 'has-items-separator': addItemsSeparator, 'always-open': isOpenList}]"
    ref="list"
    :tabindex="tabIndex"
    :style="{ 'max-height': `${listMaxHeight}px`, 'min-width': `${minWidth}`}"
    @mousedown.prevent
    @keydown.tab="onWrapperTab"
    @scroll="onListScroll">
    <li
      v-for="(item, index) in items"
      role="option"
      :aria-selected="isItemSelected(item).toString()"
      :class="['list-item-container', { pointed: isPointed(index, item, items.length) }, { selected: isItemSelected(item) || isGroupSelected(item) }, { disabled: item.disabled }]"
      :key="item[itemIdAttr]"
      :style="{ minHeight: `${itemMinHeight}px`}"
      :disabled="item.disabled"
      @click="onItemClicked(item)"
      @keydown.enter.space.prevent="onItemClicked(item)"
      @keydown.tab="onItemTab(event, index)"
      @mouseover="onItemHover(item, index)">
      <div v-if="isSpecialItem(item) || isGroupItem(item)" :class="['item', getSpecialItemClass(item)]" v-tooltip="{ content: item[itemTooltipAttr], html: false, autoHide: false }">
        <div class="item-content">
          <le-checkbox v-if="showGroupItemCheckbox(item)" :tabbable="false" :label="getItemLabel(item)" :state="getGroupItemState(item)">
            <le-text-with-highlight :text="getItemLabel(item)" :highlightText="searchTerm"></le-text-with-highlight>
          </le-checkbox>
          <span v-else>{{ item[itemValueAttr] }}</span>
        </div>
      </div>
      <div v-else :class="['item', { 'icon-item': hasIconsOnly } ]" v-tooltip="{ content: item[itemTooltipAttr], html: false, autoHide: false }">
        <div :class="['item-content', {'has-shortcut': hasShortcuts, 'under-group': isMultiSelect && groupSelection }]">
          <le-checkbox v-if="isMultiSelect" :tabbable="false" :label="getItemLabel(item)" :state="isItemSelected(item)" @focus="onItemHover(item, index)">
            <le-text-with-highlight :text="getItemLabel(item)" :highlightText="searchTerm"></le-text-with-highlight>
          </le-checkbox>
          <i v-if="hasIconsOnly" :class="[item[itemValueAttr], 'icon']"></i>
          <i v-else-if="hasIconsAndText" class="icon-with-text" :class="getIconClass(item)"></i>
          <le-text-with-highlight v-if="!isMultiSelect && (hasIconsAndText || !hasIconsOnly)" class="single-value" :text="getItemLabel(item)" :highlightText="searchTerm"></le-text-with-highlight>
          <span v-if="hasShortcuts" class="shortcut">{{getItemShortcut(item)}}</span>
        </div>
      </div>
    </li>
  </ul>
</template>

<script>
  import { includes, get } from 'lodash';
  import CONSTS from '../../../consts/consts';
  import leCheckbox from '../le-checkbox/le-checkbox';
  import leTextWithHighlight from '../le-text-with-highlight/le-text-with-highlight';

  export default {
    name: 'le-select-list',
    components: {
      leCheckbox,
      leTextWithHighlight,
    },
    props: {
      pointedItemIndex: {
        type: Number,
        required: true,
      },
      items: {
        type: Array,
        required: true,
      },
      itemIdAttr: {
        type: String,
        required: true,
      },
      itemValueAttr: {
        type: String,
        required: true,
      },
      itemSubtextAttr: {
        type: String,
      },
      groupSelection: {
        type: Boolean,
        default: false,
      },
      itemTooltipAttr: {
        type: String,
      },
      hasShortcuts: {
        type: Boolean,
        default: false,
      },
      isMultiSelect: {
        type: Boolean,
        default: false,
      },
      selectedItemsIds: {
        type: Array,
        default: () => [],
      },
      listMaxHeight: {
        type: Number,
        required: true,
      },
      itemMinHeight: {
        type: Number,
        required: true,
      },
      floatedList: {
        type: Boolean,
        default: false,
      },
      openListOnTop: {
        type: Boolean,
        default: false,
      },
      hasIconsOnly: {
        type: Boolean,
      },
      hasIconsAndText: {
        type: Boolean,
      },
      bubbleList: {
        type: Boolean,
        default: false,
      },
      bubbleListAlign: {
        type: String,
        required: true,
      },
      addItemsSeparator: {
        type: Boolean,
        default: false,
      },
      minWidth: {
        type: String,
        required: true,
      },
      isOpenList: {
        type: Boolean,
        default: false,
      },
      isOpen: {
        type: Boolean,
        default: false,
      },
      searchTerm: {
        type: String,
        default: '',
      },
    },
    computed: {
      tabIndex() {
        return this.isMultiSelect ? '-1' : '0';
      },
    },
    methods: {
      isSpecialItem(item) {
        return item.isNoItemsLabel || item.isItemCreation || item.isPlaceholderItem;
      },
      isGroupItem(item) {
        return item.isGroupLabel;
      },
      showGroupItemCheckbox(item) {
        return this.isMultiSelect && this.groupSelection && this.isGroupItem(item);
      },
      showItem(item) {
        return !(this.isSpecialItem(item) || this.isGroupItem(item));
      },
      getSpecialItemClass(item) {
        let className = '';
        if (item.isNoItemsLabel) {
          className = 'no-items';
        } else if (item.isItemCreation) {
          className = 'item-creation';
        } else if (item.isGroupLabel) {
          className = 'group-item';
        } else if (item.isPlaceholderItem) {
          className = 'placeholder-item';
        }
        return className;
      },
      isPointed(itemIndex, item, totalItems) {
        return this.pointedItemIndex === itemIndex
          && !this.isSpecialItem(item)
          && (!(this.bubbleList && totalItems <= 1));
      },
      onItemHover(item, index) {
        if (this.groupSelection && item.isGroupLabel) {
          this.$emit('groupHover', index);
        }
        if (!item.isGroupLabel && !item.isNoItemsLabel) {
          this.$emit('itemHover', index);
        }
      },
      onItemClicked(item) {
        if (this.showGroupItemCheckbox(item) && !item.disabled) {
          const groupItems = this.getGroupItems(item);
          const selectedGroupItems = this.getSelectedGroupItems(groupItems);
          const groupStatus = this.getGroupItemState(item);
          this.$emit('groupClicked', { groupItems, groupStatus, selectedGroupItems });
        }
        if (!item.isNoItemsLabel && !item.isGroupLabel && !item.disabled) {
          this.$emit('itemClicked', item);
        }
      },
      isItemSelected(item) {
        return includes(this.selectedItemsIds, item[this.itemIdAttr]);
      },
      isGroupSelected(item) {
        return this.showGroupItemCheckbox(item) && this.getGroupItemState(item) === CONSTS.LE_CHECKBOX.STATES.CHECKED;
      },
      getGroupItemState(item) {
        if (!this.groupSelection || !item.isGroupLabel) {
          return false;
        }
        const groupItemsArray = this.getGroupItems(item);
        // check if the group is selected or partially selected or empty
        const selectedGroupItems = this.getSelectedGroupItems(groupItemsArray);
        // if all selected we return all selected
        if (selectedGroupItems.length === groupItemsArray.length) {
          return CONSTS.LE_CHECKBOX.STATES.CHECKED;
        }
        // if partial selected we return partial else we return empty
        return (selectedGroupItems.length > 0) ? CONSTS.LE_CHECKBOX.STATES.INDETERMINATE : CONSTS.LE_CHECKBOX.STATES.UNCHECKED;
      },
      getSelectedGroupItems(groupItemsArray) {
        // check if the group is selected or partially selected or empty
        return groupItemsArray.reduce((selectedGroupItems, groupItem) => {
          if (this.isItemSelected(groupItem)) {
            return [...selectedGroupItems, groupItem];
          }
          return selectedGroupItems;
        }, []);
      },
      getGroupItems(groupItem) {
        const groupIndices = this.items.filter(singleItem => this.isGroupItem(singleItem)); // get all groups indices
        const groupStartIndex = this.items.indexOf(groupItem); // get the group array id
        const nextGroupItem = groupIndices[groupIndices.indexOf(groupItem) + 1]; // get next group item
        // using the next group item we determine the group ending index
        const groupEndIndex = (nextGroupItem === undefined) ? null : this.items.indexOf(nextGroupItem);
        return groupEndIndex ? this.items.slice(groupStartIndex + 1, groupEndIndex) : this.items.slice(groupStartIndex + 1); // create a group array
      },
      getItemLabel(item) {
        const subtext = this.itemSubtextAttr && item[this.itemSubtextAttr] ? ` (${item[this.itemSubtextAttr]})` : '';
        return `${item[this.itemValueAttr]}${subtext}`;
      },
      getItemShortcut(item) {
        return get(item, 'shortcut', '');
      },
      onListScroll() {
        this.$emit('scroll');
      },
      getIconClass(item) {
        return (this.hasIconsAndText && item.class) ? item.class : null;
      },
      onItemTab(event, index) {
        if (!event.shiftKey && index === this.items.length - 1) {
          this.$emit('tabout');
        }
      },
      onWrapperTab(event) {
        if (!event.shiftKey) {
          this.$emit('tabout');
        }
      },
    },
  };
</script>

<style lang="scss">
  $bubble-list-space: 20px;
  $item-content-horizontal-padding: 12px;

  .le-select-list {
    font-size: 12px;
    padding-left: 0;
    box-sizing: border-box;
    outline: none;
    margin: 0;
    @include themify {
      color: getColor('selectListItemText');
      background-color: getColor('selectListBackground');
      border: 1px solid getColor('selectListBorder');
    };
    list-style: none;
    overflow-x: hidden;
    overflow-y: auto;
    z-index: 999;

    &:focus {
      .list-item-container {
        width: calc(100% - 4px);
        margin: 0 auto;

        &:last-child {
          @include themify(null, 'show-a11y-focus-outline') {
            border-bottom: 2px solid getColor('a11yFocusIndicator') !important;
          }
        }
      }
    }

    &.floated-list {
      position: absolute;
      &.top-direction {
        bottom: 100%;
      }
    }
    &.has-items-separator {
      .list-item-container {
        @include themify {
          border-bottom: 1px solid getColor('selectListBorder');
        };
      }
    }
    .list-item-container {
      display: flex;
      align-items: center;
      box-sizing: border-box;
      cursor: pointer;
      padding: 8px 10px 8px 0px;
      &.pointed {
        @include themify {
          background-color: getColor('selectListItemBackgroundHover');
        };
      }
      &.selected {
        font-weight: bold;
      }
      &.disabled {
        opacity: 0.5;
        cursor: default;
      }
      .item {
        &.item-creation {
          .item-content {
            @include themify {
              color: getColor('selectListItemCreate');
            };
          }
        }
        &.placeholder-item {
          .item-content {
            @include themify {
              color: getColor('selectListItemPlaceholder');
            };
          }
        }
        &.icon-item {
          width: 100%;
          .item-content {
            padding: 0 8px;
            line-height: unset;
          }
        }
        .item-content {
          @include disable-user-select();
          white-space: nowrap;
          padding: 0 $item-content-horizontal-padding;
          line-height: 12px;
          @include themify {
            color: getColor('selectListItemText');
          };
          &.under-group { // if group is selectable we use margin on the left
            margin-left: 15px;
          }
          .icon-with-text {
            display: inline-block;
            vertical-align: top;
            margin-right: 5px;
          }
          .le-checkbox {
            line-height: 14px;
            .indicator {
              vertical-align: top;
              margin-right: 6px;
            }
            .label {
              font-size: 12px;
              vertical-align: middle;
            }
          }
          .single-value {
            display: inline-block;
          }

          &.has-shortcut {
            padding-right: 65px;

            .shortcut {
              position: absolute;
              font-size: 10px;
              display: inline-block;
              right: 10px;
              padding-top: 2px;
              font-size: 10px;
            }
          }
        }
      }
    }
    &.bubble-list {
      overflow: visible;
      top: calc(100% + 10px);
      border-radius: 4px;
      @include themify {
        color: getColor('selectListBubbleItemText');
        background-color: getColor('selectListBubbleBackground');
        border: 1px solid getColor('selectListBubbleBorder');
        box-shadow: 0 2px 6px 1px getColor('selectListBubbleShadow');
      };
      &.has-items-separator {
        .list-item-container {
          @include themify {
            border-bottom: 1px solid getColor('selectListBubbleBorder');
          };
          &:last-child {
            border-bottom: none;
          }
        }
      }
      &.bubble-list-align-left,
      &.bubble-list-align-right {
        @include themify {
          &:before {
            border-bottom-color: getColor('selectListBubbleBorder');
            border-width: 0 7px 7px 7px;
          }
          &:after {
            border-bottom-color: getColor('selectListBubbleBackground');
            border-width: 0 6px 6px 6px;
          }
        };
      }
      &.bubble-list-align-left {
        left: calc(-#{$bubble-list-space});
        &:before {
          left: 21px;
        }
        &:after {
          left: 22px;
          top: -5px;
        }
      }
      &.bubble-list-align-right {
        right: calc(-#{$bubble-list-space});
        &:before {
          right: 20px;
        }
        &:after {
          right: 21px;
          top: -5px;
        }
        .item {
          .item-content {
            &.has-shortcut {
              padding-right: calc(65px + #{$bubble-list-space});

              .shortcut {
                right: $item-content-horizontal-padding;
              }
            }
          }
        }
      }
      &:before, &:after {
        bottom: 100%;
        border: solid transparent;
        content: " ";
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
      }
      &.top-direction {
        &:before, &:after {
          bottom: auto;
          top: 100%;
        }
        @include themify {
          &:before {
            border-top-color: getColor('selectListBubbleBorder');
            border-width: 7px 7px 0 7px;
          }
          &:after {
            border-top-color: getColor('selectListBubbleBackground');
            border-width: 6px 6px 0 6px;
          }
        };
      }

      .list-item-container {
        &.pointed {
          @include themify {
            background-color: getColor('selectListBubbleItemBackgroundHover');
          };
        }
        .item {
          &.item-creation {
            .item-content {
              @include themify {
                color: getColor('selectListBubbleItemCreate');
              };
            }
          }
          &.no-items {
            @include themify {
              color: getColor('selectListBubbleItemText');
            };
          }
          .item-content {
            @include themify {
              color: getColor('selectListBubbleItemText');
            };
          }
        }
      }

      li {
        list-style-type: none;
        .icon {
          padding-top: 2px;
        }
      }
    }
    &.always-open {
      position: relative;
      margin-top: 2px;
      box-shadow: none;
      &:before {
        border: none;
      }
      &:after {
        border: none;
      }
    }
  }
</style>
